// @core/stores/knowledge/KnowledgeStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { knowledgeApi } from '@core/api';
import type {
  CreateKnowledgeBaseRequest,
  IndexSourceRequest,
  KnowledgeBase,
  KnowledgeSourceResponse,
  ListSourcesResponse,
  RetrieveRequest,
  RetrieveResponse,
  UpdateKnowledgeBaseRequest,
} from '@core/types/knowledge';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class KnowledgeStore {
  // List state
  items: KnowledgeBase[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: KnowledgeBase | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, KnowledgeBase>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): KnowledgeBase | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: KnowledgeBase | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create knowledge base
   */
  async CreateKnowledgeBase(data: CreateKnowledgeBaseRequest): Promise<KnowledgeBase> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await knowledgeApi.CreateKnowledgeBase(data);
      const item = response.knowledge_base;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete knowledge base
   */
  async DeleteKnowledgeBase(base_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await knowledgeApi.DeleteKnowledgeBase(base_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== base_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === base_id) {
          this.currentItem = null;
        }

        this.removeFromCache(base_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get knowledge base
   */
  async GetKnowledgeBase(base_id: string): Promise<KnowledgeBase> {
    // Check cache first
    const cached = this.cache.get(base_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await knowledgeApi.GetKnowledgeBase(base_id);
      const item = response.knowledge_base;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Index source
   */
  async IndexSource(base_id: string, data: IndexSourceRequest): Promise<KnowledgeSourceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await knowledgeApi.IndexSource(base_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List knowledge bases
   */
  async ListKnowledgeBases(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await knowledgeApi.ListKnowledgeBases(params);

      runInAction(() => {
        this.items = response.knowledge_bases?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List sources
   */
  async ListSources(base_id: string, params?: PaginatedRequest): Promise<ListSourcesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await knowledgeApi.ListSources(base_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove source
   */
  async RemoveSource(base_id: string, source_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await knowledgeApi.RemoveSource(base_id, source_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Retrieve chunks
   */
  async Retrieve(base_id: string, data: RetrieveRequest): Promise<RetrieveResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await knowledgeApi.Retrieve(base_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update knowledge base
   */
  async UpdateKnowledgeBase(base_id: string, data: UpdateKnowledgeBaseRequest): Promise<KnowledgeBase> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await knowledgeApi.UpdateKnowledgeBase(base_id, data);
      const item = response.knowledge_base;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const knowledgeStore = new KnowledgeStore();
